"use client";

import {
  forwardRef,
  useCallback,
  useRef,
  useState,
  type ComponentType,
  type MutableRefObject,
  type Ref,
} from "react";
import {
  motion as motionOriginal,
  AnimatePresence,
  useInView,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";
import { elementIntersectsExpandedRoot, scrollRevealViewport } from "@/lib/motion-viewport";

export {
  AnimatePresence,
  useInView,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
};

function assignRef<T>(ref: Ref<T> | undefined, value: T) {
  if (typeof ref === "function") ref(value);
  else if (ref != null) (ref as MutableRefObject<T | null>).current = value;
}

function isEmptyObjectInitial(initial: unknown): boolean {
  return (
    typeof initial === "object" &&
    initial !== null &&
    !Array.isArray(initial) &&
    Object.keys(initial as object).length === 0
  );
}

function wrapMotionComponent(
  Comp: ComponentType<Record<string, unknown>>,
  name: string
): ComponentType<Record<string, unknown>> {
  const Wrapped = forwardRef<unknown, Record<string, unknown>>(
    function ScrollRevealMotion(props, ref) {
      const { initial, whileInView, viewport: viewportProp, ...rest } = props;

      const [bypassInitial, setBypassInitial] = useState(false);
      const syncReported = useRef(false);

      const marginFromViewport =
        viewportProp &&
        typeof viewportProp === "object" &&
        viewportProp !== null &&
        "margin" in viewportProp
          ? String((viewportProp as { margin?: string }).margin)
          : scrollRevealViewport.margin;

      const needsScrollRevealSync =
        whileInView !== undefined && initial !== false && !isEmptyObjectInitial(initial);

      const mergedRef = useCallback(
        (node: Element | null) => {
          assignRef(ref as Ref<Element | null>, node);
          if (!node) {
            syncReported.current = false;
            return;
          }
          if (
            needsScrollRevealSync &&
            !syncReported.current &&
            elementIntersectsExpandedRoot(node, marginFromViewport)
          ) {
            syncReported.current = true;
            setBypassInitial(true);
          }
        },
        [needsScrollRevealSync, marginFromViewport, ref]
      );

      if (!needsScrollRevealSync) {
        return <Comp ref={ref as Ref<never>} {...props} />;
      }

      const effectiveInitial = bypassInitial ? false : initial;

      return (
        <Comp
          ref={mergedRef as Ref<never>}
          {...rest}
          initial={effectiveInitial}
          whileInView={whileInView}
          viewport={viewportProp}
        />
      );
    }
  );

  Wrapped.displayName = `motion.${name}`;
  return Wrapped as ComponentType<Record<string, unknown>>;
}

const wrapCache = new Map<string, ComponentType<Record<string, unknown>>>();

export const motion = new Proxy(motionOriginal, {
  get(target, prop, receiver) {
    if (prop === "create") {
      const orig = Reflect.get(target, "create", receiver) as
        | ((component: never, options?: never) => ComponentType<unknown>)
        | undefined;
      if (typeof orig !== "function") return orig;
      return (component: never, options?: never) => {
        const Created = orig(component, options) as ComponentType<Record<string, unknown>>;
        return wrapMotionComponent(Created, "create");
      };
    }

    if (typeof prop === "symbol") {
      return Reflect.get(target, prop, receiver);
    }

    const key = prop as string;
    const original = Reflect.get(target, prop, receiver);

    if (typeof original === "function") {
      if (!wrapCache.has(key)) {
        wrapCache.set(
          key,
          wrapMotionComponent(original as ComponentType<Record<string, unknown>>, key)
        );
      }
      return wrapCache.get(key)!;
    }

    return original;
  },
  apply(target, thisArg, argArray) {
    const Created = Reflect.apply(
      target as unknown as CallableFunction,
      thisArg,
      argArray
    ) as ComponentType<Record<string, unknown>>;
    return wrapMotionComponent(Created, "custom");
  },
}) as typeof motionOriginal;
